import {
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { isPrivateSellerListing } from '../../util/payoutRequirements';
import { isManualVehicleSettlement } from '../../util/settlement';

/**
 * Where a seller actually stands, computed from data the app already has.
 *
 * This exists because a seller could previously get stuck with nothing to
 * see: publishing a draft opens the payout-details modal instead of
 * publishing when Stripe is not connected, and closing that modal leaves the
 * listing a draft with no standing explanation anywhere. Account-level holds
 * (no posting permission, a listing awaiting operator approval) were equally
 * invisible from the seller's own listings page.
 *
 * Two rules run through the whole module:
 *
 *  - Never claim a step passed that was not actually checked. A missing
 *    permission set or an unfetched Stripe account produces UNKNOWN, not DONE.
 *  - Never leave a blocker unstated. Every non-DONE step carries a reason and,
 *    where the seller can act, where to go.
 */

export const STEP_STATE = {
  DONE: 'done',
  ACTION_NEEDED: 'actionNeeded',
  WAITING: 'waiting',
  UNKNOWN: 'unknown',
};

export const STEP_ID = {
  EMAIL: 'email',
  ACCOUNT: 'account',
  PAYOUT: 'payout',
  LISTING: 'listing',
};

const hasRequirements = (stripeAccountData, requirementType) =>
  Array.isArray(stripeAccountData?.requirements?.[requirementType]) &&
  stripeAccountData.requirements[requirementType].length > 0;

const emailStep = currentUser => {
  const verified = currentUser?.attributes?.emailVerified;
  return {
    id: STEP_ID.EMAIL,
    state: verified ? STEP_STATE.DONE : STEP_STATE.ACTION_NEEDED,
    // The account page is where a new verification email can be requested.
    actionRoute: verified ? null : 'ContactDetailsPage',
  };
};

const accountStep = currentUser => {
  // An unauthorized account is one the operator has not let in yet -- there is
  // nothing the seller can do but wait, and saying so beats a dead end.
  if (currentUser?.attributes?.state && currentUser.attributes.state !== 'active') {
    return { id: STEP_ID.ACCOUNT, state: STEP_STATE.WAITING, actionRoute: null };
  }

  const permissionSet = currentUser?.effectivePermissionSet;
  if (!permissionSet?.id) {
    // The relationship was not included in the fetch. We genuinely do not know
    // whether this seller may post, so we do not say that they may.
    return { id: STEP_ID.ACCOUNT, state: STEP_STATE.UNKNOWN, actionRoute: null };
  }

  const allowed = permissionSet.attributes?.postListings === 'permission/allow';
  return {
    id: STEP_ID.ACCOUNT,
    state: allowed ? STEP_STATE.DONE : STEP_STATE.WAITING,
    actionRoute: null,
  };
};

const payoutStep = ({ currentUser, listings, stripeAccount, payoutDetailsRequired }) => {
  if (!payoutDetailsRequired) return null;
  // No vehicle money is paid out through Stripe while settlement is manual, so
  // there is no payout step to complete, blocking or not (util/settlement).
  if (isManualVehicleSettlement()) return null;

  // Private-seller listings publish without payout details, so for those the
  // step is still worth completing -- nobody gets paid through Skip without it
  // -- but it is not what is standing between the seller and a live listing.
  // Saying otherwise would send them to Stripe to fix something that is not
  // blocking them. With no listings yet there is nothing to judge from, so we
  // do not claim it blocks anything.
  const blocksPublishing = (listings || []).some(l => !isPrivateSellerListing(l));

  const connected = !!currentUser?.stripeAccount?.id;
  if (!connected) {
    return {
      id: STEP_ID.PAYOUT,
      state: STEP_STATE.ACTION_NEEDED,
      actionRoute: 'StripePayoutPage',
      blocksPublishing,
      detail: blocksPublishing ? null : 'notBlocking',
    };
  }

  const stripeAccountData = stripeAccount?.attributes?.stripeAccountData;
  if (!stripeAccountData) {
    // Connected, but the account was never fetched here, so its outstanding
    // requirements are unknown. Reported as connected-but-unverified rather
    // than as complete.
    return {
      id: STEP_ID.PAYOUT,
      state: STEP_STATE.UNKNOWN,
      actionRoute: 'StripePayoutPage',
      blocksPublishing,
    };
  }

  const missing =
    hasRequirements(stripeAccountData, 'past_due') ||
    hasRequirements(stripeAccountData, 'currently_due');

  return {
    id: STEP_ID.PAYOUT,
    state: missing ? STEP_STATE.ACTION_NEEDED : STEP_STATE.DONE,
    actionRoute: missing ? 'StripePayoutPage' : null,
    blocksPublishing,
    detail: missing && !blocksPublishing ? 'notBlocking' : null,
  };
};

const listingStep = ({ listings, listingsLoaded }) => {
  if (!listingsLoaded) {
    return { id: STEP_ID.LISTING, state: STEP_STATE.UNKNOWN, actionRoute: null, detail: null };
  }

  const states = (listings || []).map(l => l?.attributes?.state);

  if (states.includes(LISTING_STATE_PUBLISHED)) {
    return { id: STEP_ID.LISTING, state: STEP_STATE.DONE, actionRoute: null, detail: 'published' };
  }
  if (states.includes(LISTING_STATE_PENDING_APPROVAL)) {
    return {
      id: STEP_ID.LISTING,
      state: STEP_STATE.WAITING,
      actionRoute: null,
      detail: 'pendingApproval',
    };
  }
  if (states.includes(LISTING_STATE_DRAFT)) {
    // Started but never published -- the single most common place to get
    // stuck, and previously the least visible.
    return {
      id: STEP_ID.LISTING,
      state: STEP_STATE.ACTION_NEEDED,
      actionRoute: null,
      detail: 'draft',
    };
  }
  return {
    id: STEP_ID.LISTING,
    state: STEP_STATE.ACTION_NEEDED,
    actionRoute: 'NewListingPage',
    detail: 'notStarted',
  };
};

/**
 * @param {Object} params
 * @param {Object} params.currentUser
 * @param {Array} params.listings own listings (any state)
 * @param {boolean} params.listingsLoaded false while the query is unresolved
 * @param {Object} [params.stripeAccount] from state.stripeConnectAccount
 * @param {boolean} [params.payoutDetailsRequired=true] mirrors the listing
 *   type config; payout details are required unless explicitly disabled
 * @returns {{steps: Array, headline: string}} ordered steps plus the single
 *   line summarising where the seller stands
 */
export const computeSellerStatus = ({
  currentUser,
  listings,
  listingsLoaded,
  stripeAccount = null,
  payoutDetailsRequired = true,
}) => {
  const steps = [
    emailStep(currentUser),
    accountStep(currentUser),
    payoutStep({ currentUser, listings, stripeAccount, payoutDetailsRequired }),
    listingStep({ listings, listingsLoaded }),
  ].filter(Boolean);

  const listing = steps.find(s => s.id === STEP_ID.LISTING);
  const blockers = steps.filter(
    s =>
      s.id !== STEP_ID.LISTING &&
      s.state !== STEP_STATE.DONE &&
      s.state !== STEP_STATE.UNKNOWN &&
      // An outstanding step that does not actually hold publishing back is
      // still shown, just not blamed for where the seller is stuck.
      s.blocksPublishing !== false
  );

  // The headline names the one thing standing in the way, in the order the
  // seller hits it. "Ready to publish" is claimed only when nothing else is
  // outstanding and a draft is actually waiting.
  const headline = (() => {
    if (listing?.detail === 'published') return 'live';
    if (listing?.detail === 'pendingApproval') return 'listingUnderReview';
    if (blockers.length > 0) return blockers[0].id;
    if (listing?.detail === 'draft') return 'readyToPublish';
    if (listing?.detail === 'notStarted') return 'notStarted';
    return 'checking';
  })();

  return { steps, headline };
};
